import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, BookOpen, Send, Inbox, CheckCircle2, Pencil, Trash2, Gift, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { STATUS_LABELS, STATUS_BADGE } from "../lib/constants";
import BookCover from "../components/BookCover";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const PROP_STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300" },
  accepted: { label: "Accettata", cls: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300" },
  rejected: { label: "Rifiutata", cls: "bg-stone-200 text-stone-700 border-stone-300 dark:bg-stone-800 dark:text-stone-400" },
  completed: { label: "Completata", cls: "bg-secondary/15 text-secondary border-secondary/30" },
};

function ProposalRow({ p, side }) {
  const other = side === "sent" ? p.owner : p.proposer;
  const st = PROP_STATUS[p.status] || {};
  return (
    <Link to={`/proposta/${p.id}`} data-testid={`proposal-row-${p.id}`} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
      <div className="w-14 h-20 rounded-lg bg-muted overflow-hidden shrink-0">
        <BookCover book={p.book} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif font-semibold truncate">{p.book?.title}</p>
        <p className="text-sm text-muted-foreground truncate">
          {side === "sent" ? "a " : "da "}{other?.name}
          {p.is_gift_request ? <span className="inline-flex items-center gap-1 ml-2 text-rose-600"><Gift className="w-3 h-3" />in regalo</span> : p.offered_book ? <> · offri «{p.offered_book.title}»</> : null}
        </p>
      </div>
      <Badge variant="outline" className={st.cls}>{st.label}</Badge>
      <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </Link>
  );
}

export default function Dashboard() {
  const [mine, setMine] = useState([]);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [done, setDone] = useState([]);

  const loadAll = useCallback(async () => {
    const [m, s, r, d] = await Promise.all([
      api.get("/books/mine"), api.get("/proposals/sent"),
      api.get("/proposals/received"), api.get("/proposals/completed"),
    ]);
    setMine(m.data); setSent(s.data); setReceived(r.data); setDone(d.data);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const changeStatus = async (bookId, status) => {
    try { await api.patch(`/books/${bookId}/status`, { status }); toast.success("Stato aggiornato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (bookId) => {
    try { await api.delete(`/books/${bookId}`); toast.success("Annuncio eliminato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pendingReceived = received.filter((p) => p.status === "pending").length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl font-serif font-bold tracking-tight">Area personale</h1>
        <Button asChild className="bg-primary hover:bg-primary/90"><Link to="/pubblica" data-testid="dashboard-publish"><Plus className="w-4 h-4 mr-2" />Pubblica</Link></Button>
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-6">
          <TabsTrigger value="mine" data-testid="tab-mine"><BookOpen className="w-4 h-4 mr-2" />I miei libri ({mine.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent"><Send className="w-4 h-4 mr-2" />Proposte inviate ({sent.length})</TabsTrigger>
          <TabsTrigger value="received" data-testid="tab-received"><Inbox className="w-4 h-4 mr-2" />Proposte ricevute ({received.length}){pendingReceived > 0 && <span className="ml-1.5 bg-primary text-primary-foreground rounded-full text-[10px] px-1.5">{pendingReceived}</span>}</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-done"><CheckCircle2 className="w-4 h-4 mr-2" />Scambi conclusi ({done.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="mine">
          {mine.length === 0 ? <Empty text="Non hai ancora pubblicato libri." cta /> : (
            <div className="space-y-3">
              {mine.map((b) => (
                <div key={b.id} data-testid={`my-book-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <Link to={`/libro/${b.id}`} className="w-14 h-20 rounded-lg bg-muted overflow-hidden shrink-0">
                      <BookCover book={b} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/libro/${b.id}`} className="font-serif font-semibold truncate block hover:text-primary">{b.title}</Link>
                      <p className="text-sm text-muted-foreground truncate">{b.author}</p>
                      <Badge variant="outline" className={`mt-1.5 ${STATUS_BADGE[b.status]}`}>{STATUS_LABELS[b.status]}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {b.status !== "exchanged" && (
                      <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                        <SelectTrigger data-testid={`status-select-${b.id}`} className="flex-1 sm:flex-none sm:w-36 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Disponibile</SelectItem>
                          <SelectItem value="suspended">Sospendi</SelectItem>
                          <SelectItem value="exchanged">Segna scambiato</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button asChild variant="outline" size="icon" className="h-9 w-9 shrink-0"><Link to={`/modifica/${b.id}`} data-testid={`edit-${b.id}`}><Pencil className="w-4 h-4" /></Link></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9 shrink-0 text-destructive" data-testid={`delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Eliminare l'annuncio?</AlertDialogTitle><AlertDialogDescription>«{b.title}» verrà rimosso definitivamente.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => remove(b.id)} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent">
          {sent.length === 0 ? <Empty text="Non hai inviato proposte." /> : <div className="space-y-3">{sent.map((p) => <ProposalRow key={p.id} p={p} side="sent" />)}</div>}
        </TabsContent>
        <TabsContent value="received">
          {received.length === 0 ? <Empty text="Non hai ricevuto proposte." /> : <div className="space-y-3">{received.map((p) => <ProposalRow key={p.id} p={p} side="received" />)}</div>}
        </TabsContent>
        <TabsContent value="done">
          {done.length === 0 ? <Empty text="Nessuno scambio concluso, per ora." /> : <div className="space-y-3">{done.map((p) => <ProposalRow key={p.id} p={p} side="sent" />)}</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ text, cta }) {
  return (
    <div className="text-center py-16 rounded-2xl border border-dashed border-border">
      <BookOpen className="w-9 h-9 mx-auto text-muted-foreground mb-3" />
      <p className="text-muted-foreground">{text}</p>
      {cta && <Button asChild className="mt-4 bg-primary hover:bg-primary/90"><Link to="/pubblica">Pubblica il tuo primo libro</Link></Button>}
    </div>
  );
}
